"use client";

import { FAQSection } from "@/types/dto/destinations-page/destination-page-types";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Stack,
  Typography,
  SxProps,
  Theme
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import React, { useState } from "react";
import sanitizeHtml from "@/utils/sanitize-html";
import { ColletteTextButton } from "@collette-travel/collette-common-controls";
import { stripBoldItalicTags } from "@/utils/content-utils";
import parseLink from "@/utils/parse-link";

export interface FaqSectionProps {
  faqSection: FAQSection;
  headerSx?: SxProps<Theme>;
  headerComponent?: React.ElementType;
  faqContainerSx?: SxProps<Theme>;
  accordionComponent?: React.ElementType;
  index?: number;
  moduleRankingPosition?: string;
  dynamicId?: string;
}

const FaqSectionComponent: React.FC<FaqSectionProps> = ({
  faqSection,
  headerSx,
  headerComponent,
  faqContainerSx,
  accordionComponent,
  index,
  moduleRankingPosition,
  dynamicId
}) => {
  const [expandedAccordions, setExpandedAccordions] = useState<Record<string, boolean>>({});
  const testIdIndexPrefix = index !== undefined ? `${index}-` : "";

  const handleAccordionChange =
    (faqId: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedAccordions((prev) => ({
        ...prev,
        [faqId]: isExpanded
      }));
    };

  const allExpanded = faqSection.faqs.every((faq) => expandedAccordions[faq.id]);

  const toggleAllAccordions = () => {
    if (allExpanded) {
      // Collapse all
      setExpandedAccordions({});
    } else {
      // Expand all
      const allExpandedState = faqSection.faqs.reduce(
        (acc, faq) => ({
          ...acc,
          [faq.id]: true
        }),
        {}
      );
      setExpandedAccordions(allExpandedState);
    }
  };

   if(faqSection?.faqs?.length === 0) {
    return null; // Don't render the FAQ section if there are no FAQs
  }

  return (
    <Stack
      sx={{
        padding: { xs: "2.4rem 0", md: "4rem 0" },
        gap: "2.5rem",
        width: "100%",
        display: "flex",
        alignItems: "center",
        ...faqContainerSx
      }}
      id={dynamicId || "faqs"}
      className="anchor-section"
      data-track="visibility"
      data-parent-container-type="faqs"
      data-module-name="FAQ Section"
      data-ranking-algorithm="manual"
      data-module-ranking-position={moduleRankingPosition}
      component="section"
    >
      <Typography
        variant="h2"
        sx={{
          paddingBottom: "1.5rem",
          fontSize: "3.2rem",
          textAlign: "center",
          ...headerSx
        }}
        data-testid={`faq-header${(index ?? 0) + 1}`}
        component={headerComponent || "h2"}
      >
        {faqSection.basicInfo.headerText}
      </Typography>

      <Box
        sx={{
          maxWidth: "700px",
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          width: "100%"
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <ColletteTextButton
            onClick={toggleAllAccordions}
            sx={{
              textTransform: "none",
              typography: "h6",
              "&:focus-visible": {
                outline: "1px solid",
                outlineColor: "primary.main",
                outlineOffset: "-1px"
              }
            }}
            testId="toggle-all-accordions"
            label={allExpanded ? "Collapse All" : "Expand All"}
          />
        </Box>
        {faqSection.faqs.map((faq, idx) => (
          <Accordion
            key={faq.id || idx}
            expanded={expandedAccordions[faq.id] || false}
            onChange={handleAccordionChange(faq.id)}
            sx={{
              mb: 2,
              boxShadow: 0,
              borderBottom: (theme) => idx !== faqSection.faqs.length - 1 ? `1px solid ${theme.palette.divider}` : "none",
              "&:before": { display: "none" },
              color: "text.primary",
              backgroundColor: "background.default",
              margin: "0",
              gap: "0.625rem",
              width: "100%"
            }}
            slotProps={{
              heading: {
                component: "div"
              }
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls={`faq-content-${faq.id}`}
              id={`faq-header-${faq.id}`}
              sx={{ margin: "0" }}
              data-track="click"
              data-faq-id={faq.id}
              data-faq-question={faq.question}
            >
              <Typography
                sx={{ fontWeight: 600 }}
                data-testid={`faq-question-${testIdIndexPrefix}${idx}`}
                component={accordionComponent || "h3"}
              >
                {faq.question}
              </Typography>
            </AccordionSummary>
            <AccordionDetails
              sx={{
                fontSize: "1.6rem",
                fontStyle: "normal",
                fontWeight: 500,
                lineHeight: "24px",
                letterSpacing: "0.025rem",
                paddingY: "0px",
                marginTop: "-16px"
              }}
            >
              <Box
                dangerouslySetInnerHTML={{ __html: parseLink(stripBoldItalicTags(sanitizeHtml(faq.answer))) }}
                id="customFAQAnswer"
                data-testid={`faq-answer-${testIdIndexPrefix}${idx}`}
                sx={{
                  marginY: faq.answer.includes("<p>") ? 0 : "2rem",
                  "& p": {
                    marginY: faq.answer.includes("<p>") ? "2rem" : 0
                  }
                }}
              />
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    </Stack>
  );
};
export default FaqSectionComponent;
